/** Pure label decisions for the Book Data duplicates panel's density pass.
 *
 *  The panel's prose bands compress into chips and ⓘ popovers; these helpers
 *  own the wording so the figures are pinned by test — the plan's rule that
 *  every number survives a density pass, only geometry changes.
 */

const fmt = new Intl.NumberFormat('en-IN')

export interface RecordedSummary {
  groups: number
  rows_disposed: number
  value: string
  awaiting_checker: number
}

/** The "already decided" alert as one title-row chip: yellow while any
 *  verdict still awaits its checker (written down, not judged — the state
 *  that gates work stays glanceable), green once every decision is through. */
export function decidedChip(
  r: RecordedSummary,
): { label: string; color: 'yellow' | 'green' } | null {
  if (r.groups === 0) return null
  if (r.awaiting_checker >= r.groups && r.groups > 0) {
    // awaiting is a SUBSET of decided by construction (keep_one.py builds it
    // by iterating the decided groups) — printing "119 · 119" read as two
    // different quantities. One set, stated once.
    return {
      label: `${fmt.format(r.groups)} decided — all awaiting checker`,
      color: 'yellow',
    }
  }
  if (r.awaiting_checker > 0) {
    return {
      label: `${fmt.format(r.groups)} decided · ${fmt.format(r.awaiting_checker)} awaiting checker`,
      color: 'yellow',
    }
  }
  return { label: `${fmt.format(r.groups)} decided`, color: 'green' }
}

/** The Copies-column note said "+1 other line(s) on this document" — the
 *  evidence rows underneath already say it in full, so the cell keeps only
 *  the count. */
export function extraLinesNote(extra: number): string | null {
  if (extra <= 0) return null
  return `+${fmt.format(extra)} line${extra === 1 ? '' : 's'}`
}

/** The corrections sentence as a chip-strip item; the full sentence (originals
 *  kept, next run re-validates) moves behind the ⓘ beside it. */
export function correctionChipLabel(editedRows: number, debitDelta: string): string {
  const d = Number(debitDelta)
  return `${fmt.format(editedRows)} corrected · TDS ${d >= 0 ? 'up' : 'down'} ₹${Math.abs(d)
    .toLocaleString('en-IN')}`
}
